import { Fragment, lazy, Suspense, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { journeyApi } from '../../api/journeyApi';
import { journeyPlanningApi } from '../../api/journeyPlanningApi';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { DEFAULT_LOCALE } from '@formosa/shared/i18n/translate';
import { useAuth } from '../../auth/AuthContext';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { ErrorState, LoadingState } from '../../components/ui';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import {
  ACTIVITY_TYPES,
  activityLocationDisplay,
  buildPlanningMapMarkers,
  buildDraftPreviewMapMarker,
  classifyPlanningError,
  encodeLocationSummary,
  normalizeActivityDetail,
  normalizePlanningSchedule,
  planningCapabilities,
  planningConflictMessage,
  buildTimedActivityDefaults,
  snowflakeId,
  toApiLocalTime,
  validateActivityTimes,
} from './planningModel';
import {
  EXTERNAL_MAP_NAVIGATION_LINK,
  isActivityExternalMapNavigationEligible,
  resolveExternalMapNavigation,
} from './externalMapNavigation';
import {
  activityStatusProductLabel,
  activityTypeProductLabel,
  formatPlanningDayHeading,
  formatPlanningDayNavLabel,
  journeyHasDateRange,
} from './planningPresentation';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import JourneyContinuationSourceNote from '../journey-workspace/JourneyContinuationSourceNote';
import ActivityLocationPicker from './ActivityLocationPicker';
import CustomLocationCreatePanel from './CustomLocationCreatePanel';
import ActivityRoutingEstimate, { activityLocationId } from './ActivityRoutingEstimate';
import JourneyPublicationGuidedFlow from '../journey-publication/JourneyPublicationGuidedFlow';
import { journeyPublicationApi } from '../../api/journeyPublicationApi';
import {
  emptyPublication,
  isPublicationAbsentError,
  isPublicationPublished,
  normalizeJourneyPublication,
  planningPublishEntryLabel,
  publicationRefreshPresentation,
  PUBLISHED_EDIT_UNSAVED_LEAVE_MESSAGE,
} from '../journey-publication/journeyPublicationModel';
import {
  createExternalLocationSearchProvider,
  readGooglePlacesBrowserKey,
  resolveExternalSearchModeFromEnv,
} from './externalLocationProvider';
import { externalLocationApi } from '../../api/externalLocationApi';
import { resolveExternalPlaceToLocationNode } from './resolveExternalPlaceToLocationNode';
import {
  confirmDiscardUnsavedChanges,
  UNSAVED_LEAVE_MESSAGE,
  useBeforeUnloadWhen,
} from '../ux-closure/unsavedChangesGuard';
import '../journey-workspace/journey-workspace.css';
import './journey-planning.css';
import {
  clearHandoffSession,
  consumeHandoffNonce,
  isHandoffNonceConsumed,
  locationNodeFromHandoff,
  readHandoffFromLocationState,
  readHandoffSession,
  stripHandoffFromLocationState,
} from '../personal-map/personalMapJourneyHandoff';
import { getPersonalMapMessage } from '../../i18n/personalMapMessages';
import { PLANNING_FEEDBACK_KEYS } from '../../i18n/planningFeedbackMessages';

const activitySearchProvider = createExternalLocationSearchProvider({
  mode: resolveExternalSearchModeFromEnv(),
  browserApiKey: readGooglePlacesBrowserKey(),
});

const PlanningMapSurface = lazy(() => import('./PlanningMapSurface'));

/**
 * New activity draft. When dayActivities is provided (add flow init only),
 * prefill smart-next start + default end. Do not call again on schedule refresh.
 * timeSuggestionSource: 'smart-next' | null — drives the smart-next hint only.
 */
function emptyActivityDraft(dayActivities, { excludeActivityId } = {}) {
  if (!Array.isArray(dayActivities)) {
    return {
      title: '',
      plannedStartTime: '',
      plannedEndTime: '',
      timeMode: 'none',
      timeSuggestionSource: null,
      description: '',
      activityType: 'CUSTOM',
      location: '',
      locationNode: null,
    };
  }
  const timed = buildTimedActivityDefaults(dayActivities, { excludeActivityId });
  return {
    title: '',
    plannedStartTime: timed.plannedStartTime,
    plannedEndTime: timed.plannedEndTime,
    timeMode: timed.timeMode,
    // 僅在確實接續前一筆時標記 smart-next，避免首筆誤顯示「已依前一筆」。
    timeSuggestionSource: timed.fromPriorActivity ? 'smart-next' : null,
    description: '',
    activityType: 'CUSTOM',
    location: '',
    locationNode: null,
  };
}

function locationNodeFromActivity(activity) {
  if (activity?.location?.id) {
    return {
      locationId: activity.location.id,
      displayName: activity.location.name,
      latitude: activity.location.latitude ?? null,
      longitude: activity.location.longitude ?? null,
      address: activity.location.address ?? null,
    };
  }
  if (activity?.locationId) {
    return {
      locationId: activity.locationId,
      displayName: activity.locationLabel || '已選地點',
    };
  }
  return null;
}

const testIdSelector = (testId) => `[data-testid="${testId}"]`;
const activitySelector = (activityId) => testIdSelector(`planning-activity-${activityId}`);

/** 與日期 chip 相同的「第 N 天」編號：displayOrder 有效時使用，否則以索引補位。 */
function dayNumberOf(day, index) {
  const order = Number(day?.displayOrder);
  return Number.isInteger(order) && order >= 1 ? order : index + 1;
}

/**
 * 活動離開原日（刪除或留在原日的跨日移動）後的焦點候選：
 * 同日上一筆 → 下一筆 → 新增安排 → 當日標題。依操作前的排程計算。
 */
function neighbourTargets(day, activityId) {
  const siblings = day?.activities || [];
  const index = siblings.findIndex((item) => item.activityId === activityId);
  const previousId = index > 0 ? siblings[index - 1].activityId : '';
  const nextId = index >= 0 && index < siblings.length - 1 ? siblings[index + 1].activityId : '';
  return [
    previousId && activitySelector(previousId),
    nextId && activitySelector(nextId),
    testIdSelector('planning-add-activity'),
    day?.journeyDayId && `[id="planning-day-heading-${day.journeyDayId}"]`,
  ].filter(Boolean);
}

function activityTimeLabel(activity) {
  const range = [activity.plannedStartTime, activity.plannedEndTime].filter(Boolean).join(' – ');
  return range || '時間未定';
}

export default function JourneyPlanningPage() {
  const { t, tf } = useTravelTranslation();
  const { journeyId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const focusActivityId = searchParams.get('activity') || '';
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);
  const sharingLock = useRef(false);
  const editLoadGeneration = useRef(0);
  const editingActivityIdRef = useRef(null);
  const addingForDayIdRef = useRef(null);
  const mapPickGeneration = useRef(0);

  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [banner, setBanner] = useState(() => (
    typeof location.state?.notice === 'string' ? location.state.notice : ''
  ));
  const [busy, setBusy] = useState(false);

  const [selectedDayId, setSelectedDayId] = useState('');
  const [addingDay, setAddingDay] = useState(false);
  const [dayDraft, setDayDraft] = useState({ localDate: '', title: '' });
  const [editingDayId, setEditingDayId] = useState(null);
  const [dayEdit, setDayEdit] = useState({ localDate: '', title: '' });

  const [addingForDayId, setAddingForDayId] = useState(null);
  const [activityDraft, setActivityDraft] = useState(emptyActivityDraft());
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [activityEdit, setActivityEdit] = useState(emptyActivityDraft());
  const [activityEditReady, setActivityEditReady] = useState(false);
  const [mapPickPhase, setMapPickPhase] = useState('idle');
  const [mapPickError, setMapPickError] = useState('');
  const [formError, setFormError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sharingActivityId, setSharingActivityId] = useState(null);
  const [shareNotice, setShareNotice] = useState('');
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [publishPanelOpen, setPublishPanelOpen] = useState(false);
  const [publicationSummary, setPublicationSummary] = useState(null);
  const [personalMapHandoff, setPersonalMapHandoff] = useState(null);
  const [personalMapHandoffHint, setPersonalMapHandoffHint] = useState('');
  const [personalMapDuplicateWarning, setPersonalMapDuplicateWarning] = useState(false);
  const personalMapHandoffAppliedRef = useRef(false);
  const handledEntryKeyRef = useRef(null);
  const composeFocusPendingRef = useRef(false);
  const composeTitleInputRef = useRef(null);
  const handledActivityFocusKeyRef = useRef(null);
  // 寫入成功後的回饋：只存 key／狀態，渲染時才依語系取文案。
  const currentJourneyIdRef = useRef(journeyId);
  const translateRef = useRef(t);
  const focusIntentRef = useRef(null);
  const retryRefreshSeqRef = useRef(0);
  const latestScheduleRef = useRef(null);
  // 跨日移動的待選目標日：以 activityId 為 key，避免多列選單互相污染；選了不送出，按「移動」才提交。
  const [pendingMoveTargets, setPendingMoveTargets] = useState({});
  const [liveFeedback, setLiveFeedback] = useState(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [refreshRetrying, setRefreshRetrying] = useState(false);
  const [focusRequest, setFocusRequest] = useState(null);
  const [publicationReloadSeq, setPublicationReloadSeq] = useState(0);

  const dayDateId = useId();
  const dayTitleId = useId();

  useEffect(() => {
    const fromState = readHandoffFromLocationState(location.state);
    const fromSession = readHandoffSession();
    const sessionMatches = Boolean(
      fromSession
      && (!fromSession.journeyId || String(fromSession.journeyId) === String(journeyId)),
    );
    if (fromSession && !sessionMatches) {
      clearHandoffSession();
    }
    const incoming = fromState || (sessionMatches ? fromSession : null);
    const notice = typeof location.state?.notice === 'string' ? location.state.notice : '';
    if (notice) setBanner(notice);

    if (incoming && !isHandoffNonceConsumed(incoming.handoffNonce)) {
      setPersonalMapHandoff(incoming);
      clearHandoffSession();
    }

    const hasHandoffInState = Boolean(location.state?.personalMapJourneyHandoff);
    if (notice || hasHandoffInState) {
      const rest = stripHandoffFromLocationState(location.state || {});
      delete rest.notice;
      navigate(location.pathname + location.search, { replace: true, state: rest });
    }
  }, [location.pathname, location.search, location.state, navigate, journeyId]);

  // Keep editor target refs in sync for in-flight map place-pick stale guards.
  useEffect(() => {
    editingActivityIdRef.current = editingActivityId;
  }, [editingActivityId]);

  useEffect(() => {
    addingForDayIdRef.current = addingForDayId;
  }, [addingForDayId]);

  // Invalidate in-flight edit detail fetches when day changes or page unmounts.
  useEffect(() => {
    editLoadGeneration.current += 1;
    mapPickGeneration.current += 1;
    editingActivityIdRef.current = null;
    setEditingActivityId(null);
    setActivityEditReady(false);
    setFormError('');
    setMapPickPhase('idle');
    setMapPickError('');
  }, [selectedDayId]);

  useEffect(() => () => {
    editLoadGeneration.current += 1;
    mapPickGeneration.current += 1;
    editingActivityIdRef.current = null;
  }, []);

  // Personal Map PM-3：一次性開啟 Activity Create 並預填 locationId（不自動 submit）。
  useEffect(() => {
    if (phase !== 'ready' || !schedule || !journey || !personalMapHandoff) return;
    if (personalMapHandoffAppliedRef.current) return;
    if (isHandoffNonceConsumed(personalMapHandoff.handoffNonce)) {
      setPersonalMapHandoff(null);
      return;
    }

    const caps = planningCapabilities(journey, schedule);
    const locationNode = locationNodeFromHandoff(personalMapHandoff);
    if (!caps.canCreateActivity || !locationNode) {
      personalMapHandoffAppliedRef.current = true;
      setPersonalMapHandoffHint(
        getPersonalMapMessage(DEFAULT_LOCALE, 'travel.personalMap.addToJourney.handoffInvalid'),
      );
      setPersonalMapHandoff(null);
      return;
    }

    const targetDayId = personalMapHandoff.journeyDayId || '';
    if (!targetDayId) {
      // Create continuation：尚無 Day — 保留 handoff，提示使用者選日後新增。
      personalMapHandoffAppliedRef.current = true;
      setPersonalMapHandoffHint(
        getPersonalMapMessage(DEFAULT_LOCALE, 'travel.personalMap.addToJourney.prefillHint'),
      );
      return;
    }

    const day = schedule.days.find((item) => item.journeyDayId === targetDayId);
    if (!day) {
      personalMapHandoffAppliedRef.current = true;
      setPersonalMapHandoffHint(
        getPersonalMapMessage(DEFAULT_LOCALE, 'travel.personalMap.addToJourney.handoffInvalid'),
      );
      return;
    }

    personalMapHandoffAppliedRef.current = true;
    const draft = emptyActivityDraft(day.activities);
    draft.locationNode = locationNode;
    setSelectedDayId(targetDayId);
    setAddingForDayId(targetDayId);
    addingForDayIdRef.current = targetDayId;
    setActivityDraft(draft);
    setFormError('');
    setPersonalMapHandoffHint(
      getPersonalMapMessage(DEFAULT_LOCALE, 'travel.personalMap.addToJourney.prefillHint'),
    );
    setPersonalMapDuplicateWarning(
      day.activities.some((act) => String(act.locationId || '') === locationNode.locationId),
    );
  }, [phase, schedule, journey, personalMapHandoff]);

  useEffect(() => {
    translateRef.current = t;
  }, [t]);

  /**
   * 讀取 Journey 與排程。soft=true 用於寫入成功後的重讀：不切回 loading、不卸載整頁，
   * 也不清除使用者新開的表單；失敗時回傳 'failed' 交由呼叫端提示，不改變 phase。
   * 回傳 'ok' | 'failed' | 'stale'（已被較新的請求或換 Journey 取代，呼叫端應直接忽略）。
   */
  const fetchPlanning = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) {
      setPhase('loading');
      setError('');
      setErrorModel(null);
      setFormError('');
      setConfirmDelete(null);
      setRefreshFailed(false);
      setRefreshRetrying(false);
      setLiveFeedback(null);
      setFocusRequest(null);
      setPendingMoveTargets({});
      focusIntentRef.current = null;
    }
    try {
      const [detailPayload, schedulePayload] = await Promise.all([
        journeyApi.getDetail(journeyId),
        journeyPlanningApi.getSchedule(journeyId),
      ]);
      if (requestId !== generation.current) return 'stale';
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      const nextSchedule = normalizePlanningSchedule(schedulePayload);
      latestScheduleRef.current = nextSchedule;
      setSchedule(nextSchedule);
      setSelectedDayId((current) => {
        if (current && nextSchedule.days.some((day) => day.journeyDayId === current)) return current;
        return nextSchedule.days[0]?.journeyDayId || '';
      });
      setRefreshFailed(false);
      setPhase('ready');
      return 'ok';
    } catch (cause) {
      if (requestId !== generation.current) return 'stale';
      if (soft) return 'failed';
      const kind = classifyPlanningError(cause);
      if (kind === 'not-found' || classifyJourneyAccessError(cause) === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || classifyJourneyAccessError(cause) === 'forbidden') setPhase('forbidden');
      else {
        setError('');
        setErrorModel(mapApiErrorToUiModel(cause, { t: translateRef.current }));
        setPhase('error');
      }
      return 'failed';
    }
  }, [journeyId, viewerMemberId]);

  const load = useCallback(() => fetchPlanning(), [fetchPlanning]);

  useEffect(() => {
    currentJourneyIdRef.current = journeyId;
  }, [journeyId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => {
      generation.current += 1;
    };
  }, [journeyId, load]);

  useEffect(() => {
    if (phase !== 'ready' || !focusActivityId || !schedule) return undefined;
    if (schedule.journeyId && schedule.journeyId !== journeyId) return undefined;
    // 每個導航入口只套用一次；寫入後的 soft refresh 不得把使用者拉回 ?activity= 所在日。
    const entryKey = `${journeyId}|${location.key}|${focusActivityId}`;
    if (handledActivityFocusKeyRef.current === entryKey) return undefined;
    handledActivityFocusKeyRef.current = entryKey;
    const owningDay = schedule.days.find((day) => (
      day.activities.some((activity) => activity.activityId === focusActivityId)
    ));
    if (owningDay) setSelectedDayId(owningDay.journeyDayId);
    document.getElementById(`planning-activity-${focusActivityId}`)?.scrollIntoView({ block: 'center' });
    return undefined;
  }, [phase, focusActivityId, schedule, journeyId, location.key]);

  // 任務首頁直達：目前 Journey 成功載入後，每個導航入口處理一次 ?day= / ?compose=1；
  // activity 聚焦與個人地圖接續優先。同一頁面實例切換 Journey 時，舊行程資料不得消費新入口。
  // day ID 以字串比對；不存在的 day 維持既有預設日，無新增權限不開表單。
  useEffect(() => {
    if (phase !== 'ready' || !schedule || !journey) return;
    if (schedule.journeyId !== journeyId || String(journey.journeyId ?? '') !== journeyId) return;
    const dayParam = searchParams.get('day');
    const composeParam = searchParams.get('compose');
    if (dayParam == null && composeParam == null) return;
    if (handledEntryKeyRef.current === location.key) return;
    handledEntryKeyRef.current = location.key;

    const deferToOtherEntry = Boolean(focusActivityId) || Boolean(personalMapHandoff);
    const targetDay = dayParam
      ? schedule.days.find((day) => day.journeyDayId === dayParam)
      : null;
    if (!deferToOtherEntry && targetDay) {
      setSelectedDayId(targetDay.journeyDayId);
      if (composeParam === '1' && planningCapabilities(journey, schedule).canCreateActivity) {
        setAddingForDayId(targetDay.journeyDayId);
        addingForDayIdRef.current = targetDay.journeyDayId;
        setActivityDraft(emptyActivityDraft(targetDay.activities));
        setPersonalMapDuplicateWarning(false);
        setFormError('');
        composeFocusPendingRef.current = true;
      }
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('day');
    nextParams.delete('compose');
    const nextSearch = nextParams.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '', hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [
    phase, schedule, journey, journeyId, searchParams, focusActivityId, personalMapHandoff,
    location.key, location.pathname, location.hash, location.state, navigate,
  ]);

  useEffect(() => {
    if (!composeFocusPendingRef.current || !addingForDayId || !composeTitleInputRef.current) return;
    composeFocusPendingRef.current = false;
    composeTitleInputRef.current.focus();
  }, [addingForDayId, selectedDayId]);

  // 排程更新後清掉失效的待選目標：活動已移除、目標日不存在，或活動已在目標日。
  useEffect(() => {
    if (!schedule) return;
    setPendingMoveTargets((current) => {
      const entries = Object.entries(current);
      if (entries.length === 0) return current;
      const dayOfActivity = new Map();
      schedule.days.forEach((day) => {
        day.activities.forEach((item) => dayOfActivity.set(item.activityId, day.journeyDayId));
      });
      const dayIds = new Set(schedule.days.map((day) => day.journeyDayId));
      const next = {};
      entries.forEach(([activityId, targetDayId]) => {
        const currentDayId = dayOfActivity.get(activityId);
        if (currentDayId && dayIds.has(targetDayId) && currentDayId !== targetDayId) next[activityId] = targetDayId;
      });
      return Object.keys(next).length === entries.length ? current : next;
    });
  }, [schedule]);

  function setPendingMoveTarget(activityId, targetDayId) {
    setPendingMoveTargets((current) => {
      if (!targetDayId) {
        if (!(activityId in current)) return current;
        const { [activityId]: _removed, ...rest } = current;
        return rest;
      }
      return current[activityId] === targetDayId ? current : { ...current, [activityId]: targetDayId };
    });
  }

  // 使用者任何後續指標／鍵盤操作都取消尚未套用的焦點意圖，refresh 完成後不得搶回焦點。
  useEffect(() => {
    function cancelPendingFocus() {
      focusIntentRef.current = null;
    }
    document.addEventListener('pointerdown', cancelPendingFocus, true);
    document.addEventListener('keydown', cancelPendingFocus, true);
    return () => {
      document.removeEventListener('pointerdown', cancelPendingFocus, true);
      document.removeEventListener('keydown', cancelPendingFocus, true);
    };
  }, []);

  // 寫入與重讀都成功後，依序聚焦第一個存在且未停用的候選目標（以 activityId／dayId 字串組成）；
  // 意圖已被取消、換 Journey、換日，或焦點已移到發起操作以外的元素時放棄。
  useEffect(() => {
    if (!focusRequest || focusIntentRef.current !== focusRequest) return;
    focusIntentRef.current = null;
    if (focusRequest.journeyId !== journeyId || focusRequest.dayId !== selectedDayId) return;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected && active !== focusRequest.origin) return;
    const target = focusRequest.targets
      .map((selector) => document.querySelector(selector))
      .find((element) => element && !element.disabled);
    target?.focus();
  }, [focusRequest, journeyId, selectedDayId]);

  // Epic 6 Slice 1：selectedDayId 變更時將 active day chip 捲入 day-nav 可見區（不改 selection / focus）。
  useEffect(() => {
    if (phase !== 'ready' || !selectedDayId) return undefined;
    const chip = document.querySelector(
      `[data-testid="planning-day-chip-${selectedDayId}"]`,
    );
    if (chip && typeof chip.scrollIntoView === 'function') {
      chip.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    return undefined;
  }, [phase, selectedDayId]);

  // publicationReloadSeq：寫入後 soft refresh 不重掛整頁，需另外觸發重讀發布狀態。
  useEffect(() => {
    if (phase !== 'ready' || !journey?.journeyId || journey.requesterRole !== 'OWNER') {
      setPublicationSummary(null);
      return undefined;
    }
    let cancelled = false;
    journeyPublicationApi.getByJourney(journey.journeyId).then((payload) => {
      if (cancelled) return;
      setPublicationSummary(normalizeJourneyPublication(payload, journey.journeyId));
    }).catch((cause) => {
      if (cancelled) return;
      if (isPublicationAbsentError(cause)) {
        setPublicationSummary(emptyPublication(journey.journeyId));
        return;
      }
      setPublicationSummary(emptyPublication(journey.journeyId));
    });
    return () => {
      cancelled = true;
    };
  }, [phase, journey?.journeyId, journey?.requesterRole, publicationReloadSeq]);

  const caps = planningCapabilities(journey, schedule);
  const publishEntryLabel = planningPublishEntryLabel(publicationSummary, {
    requesterRole: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
  });
  const mapMarkers = buildPlanningMapMarkers(schedule);
  const datedJourney = journeyHasDateRange(journey);
  const selectedDay = (schedule?.days || []).find((day) => day.journeyDayId === selectedDayId)
    || schedule?.days?.[0]
    || null;

  const draftEditorOpen = (addingForDayId && selectedDay && addingForDayId === selectedDay.journeyDayId)
    || Boolean(editingActivityId);
  const previewDraft = (editingActivityId ? activityEdit : activityDraft);
  const previewLocationNode = draftEditorOpen ? previewDraft?.locationNode : null;
  const previewKey = previewLocationNode?.locationId
    ? `draft-preview:${previewLocationNode.locationId}`
    : '';
  const draftPreviewMarker = selectedDay
    ? buildDraftPreviewMapMarker({
      previewKey,
      draftTitle: previewDraft?.title,
      dayLabel: formatPlanningDayHeading(selectedDay),
      locationNode: previewLocationNode,
    })
    : null;
  const persistedMarkersForPreview = editingActivityId
    ? mapMarkers.filter((m) => m.key !== editingActivityId)
    : mapMarkers;
  const mapMarkersWithPreview = draftPreviewMarker
    ? [...persistedMarkersForPreview, draftPreviewMarker]
    : persistedMarkersForPreview;
  const mapSelectedKey = draftPreviewMarker ? draftPreviewMarker.key : selectedActivityId;
  const mapPlacePickEnabled = Boolean(
    draftEditorOpen
    && !busy
    && mapPickPhase !== 'loading'
    && (!editingActivityId || activityEditReady)
    && (editingActivityId ? caps.canEditActivity : caps.canCreateActivity),
  );

  // JRN-09：活動表單開啟即視為 dirty（純 FE leave confirm）。
  useBeforeUnloadWhen(draftEditorOpen);

  function unsavedLeaveMessage() {
    return isPublicationPublished(publicationSummary)
      ? PUBLISHED_EDIT_UNSAVED_LEAVE_MESSAGE
      : UNSAVED_LEAVE_MESSAGE;
  }

  function handleMapSelectMarker(markerKey) {
    if (!markerKey) return;
    if (draftPreviewMarker && markerKey === draftPreviewMarker.key) return;
    selectMappedActivity(markerKey);
  }

  async function handleMapPlacePick({ provider, providerPlaceId }) {
    if (!mapPlacePickEnabled) return;
    // Capture editor target at pick start; discard if user switches editor before resolve.
    const targetEditId = editingActivityIdRef.current;
    const targetAddDayId = addingForDayIdRef.current;
    const token = ++mapPickGeneration.current;
    setMapPickPhase('loading');
    setMapPickError('');
    try {
      const node = await resolveExternalPlaceToLocationNode(
        { provider, providerPlaceId },
        externalLocationApi.resolve,
      );
      if (token !== mapPickGeneration.current) return;
      if (targetEditId) {
        if (editingActivityIdRef.current !== targetEditId) {
          setMapPickPhase('idle');
          return;
        }
        setActivityEdit((current) => ({ ...current, locationNode: node }));
      } else if (targetAddDayId) {
        if (addingForDayIdRef.current !== targetAddDayId || editingActivityIdRef.current) {
          setMapPickPhase('idle');
          return;
        }
        setActivityDraft((current) => ({ ...current, locationNode: node }));
      } else {
        setMapPickPhase('idle');
        return;
      }
      setMapPickPhase('idle');
    } catch {
      if (token !== mapPickGeneration.current) return;
      setMapPickPhase('error');
      setMapPickError('無法解析此地點，請改用搜尋或稍後再試。');
    }
  }

  function discardOpenActivityEditor() {
    mapPickGeneration.current += 1;
    editLoadGeneration.current += 1;
    editingActivityIdRef.current = null;
    addingForDayIdRef.current = null;
    setEditingActivityId(null);
    setActivityEditReady(false);
    setAddingForDayId(null);
    setActivityDraft(emptyActivityDraft());
    setFormError('');
    setMapPickPhase('idle');
    setMapPickError('');
  }

  function requestSelectedDayChange(nextDayId) {
    if (!nextDayId || nextDayId === selectedDayId) return true;
    if (!confirmDiscardUnsavedChanges(draftEditorOpen, unsavedLeaveMessage())) return false;
    if (draftEditorOpen) discardOpenActivityEditor();
    focusIntentRef.current = null;
    setLiveFeedback(null);
    setSelectedDayId(nextDayId);
    return true;
  }

  function selectMappedActivity(activityId) {
    if (!activityId || !schedule) {
      setSelectedActivityId(activityId || '');
      return;
    }
    const owningDay = schedule.days.find((day) => (
      day.activities.some((activity) => activity.activityId === activityId)
    ));
    if (owningDay && !requestSelectedDayChange(owningDay.journeyDayId)) return;
    setSelectedActivityId(activityId);
    document.getElementById(`planning-activity-${activityId}`)?.scrollIntoView?.({ block: 'nearest' });
  }

  function renderExternalMapNavigationLink(activity) {
    if (!isActivityExternalMapNavigationEligible(activity)) return null;
    const resolved = resolveExternalMapNavigation(activity);
    if (!resolved.available || !resolved.href) return null;
    return (
      <a
        className="journey-planning__external-map-link"
        href={resolved.href}
        target={EXTERNAL_MAP_NAVIGATION_LINK.target}
        rel={EXTERNAL_MAP_NAVIGATION_LINK.rel}
        data-testid="planning-external-map-nav"
        data-activity-id={activity.activityId}
        data-nav-source={resolved.source}
        aria-label={`在 Google 地圖開啟 ${activity.title}`}
      >
        在 Google 地圖開啟
      </a>
    );
  }

  async function shareActivityToChat(activity) {
    if (sharingLock.current || !activity?.activityId) return;
    sharingLock.current = true;
    setSharingActivityId(activity.activityId);
    setShareNotice('');
    setFormError('');
    try {
      await journeyPlanningApi.shareActivityToChat(journey.journeyId, activity.activityId, {
        confirm: true,
      });
      setShareNotice(`「${activity.title}」已分享到旅程聊天室`);
    } catch (cause) {
      const kind = classifyPlanningError(cause);
      if (kind === 'forbidden') setFormError('你沒有分享此安排的權限。');
      else if (kind === 'conflict') setFormError(planningConflictMessage(cause));
      else if (kind === 'unavailable') setFormError('聊天室目前無法使用，請稍後再試。');
      else {
        setFormError(resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed') || '分享到旅程聊天室失敗。');
      }
    } finally {
      sharingLock.current = false;
      setSharingActivityId(null);
    }
  }

  /**
   * 寫入 → soft refresh。寫入失敗沿用既有錯誤且不重讀；寫入成功但重讀失敗時只提示並提供重新讀取，
   * 不重送寫入。換 Journey 或被較新請求取代的結果一律忽略。
   * feedback：{ key, values } 成功播報（values 只放使用者原文或數字，不翻譯）。
   * focus：{ dayId, switchToDayId?, targets(result), stayTargets? } — targets 回傳依優先序排列的 selector；
   * switchToDayId 用於跨日移動，僅在意圖仍有效、目標日存在且沒有開著的活動表單時切換選取日；
   * 不切換時改用 stayTargets（留在原日的鄰近焦點），不繞過未儲存變更的確認。
   */
  async function runMutation(work, { feedback = null, focus = null } = {}) {
    if (mutating.current) return;
    mutating.current = true;
    const mutationJourneyId = journeyId;
    // 寫入前先建立待定焦點意圖：寫入期間的任何使用者操作都會將其取消。
    const pendingIntent = {
      journeyId: mutationJourneyId,
      dayId: focus?.dayId || '',
      switchToDayId: focus?.switchToDayId || '',
      targets: [],
      stayTargets: [],
      origin: document.activeElement,
    };
    focusIntentRef.current = focus ? pendingIntent : null;
    const isStillCurrentJourney = () => currentJourneyIdRef.current === mutationJourneyId;
    setBusy(true);
    setFormError('');
    setBanner('');
    setLiveFeedback(null);
    try {
      const result = await work(isStillCurrentJourney);
      if (!isStillCurrentJourney()) return;
      let intent = null;
      if (focus && focusIntentRef.current === pendingIntent) {
        pendingIntent.targets = (focus.targets(result) || []).filter(Boolean);
        pendingIntent.stayTargets = (focus.stayTargets?.() || []).filter(Boolean);
        if (pendingIntent.targets.length > 0) intent = pendingIntent;
      }
      focusIntentRef.current = intent;
      const outcome = await fetchPlanning({ soft: true });
      if (outcome === 'stale' || currentJourneyIdRef.current !== mutationJourneyId) return;
      if (outcome === 'failed') {
        if (focusIntentRef.current === intent) focusIntentRef.current = null;
        setRefreshFailed(true);
        return;
      }
      setPublicationReloadSeq((seq) => seq + 1);
      if (feedback) setLiveFeedback(feedback);
      if (intent && focusIntentRef.current === intent) {
        const switchTo = intent.switchToDayId;
        if (switchTo) {
          const targetExists = latestScheduleRef.current?.days.some((day) => day.journeyDayId === switchTo);
          // 有活動編輯／新增表單開著時，自動換日會略過未儲存確認並清掉或藏起草稿：留在原日。
          const editorOpen = Boolean(editingActivityIdRef.current || addingForDayIdRef.current);
          if (targetExists && !editorOpen) {
            setSelectedDayId(switchTo);
            intent.dayId = switchTo;
          } else {
            intent.targets = intent.stayTargets;
          }
        }
        if (intent.targets.length > 0) setFocusRequest(intent);
        else focusIntentRef.current = null;
      }
    } catch (cause) {
      if (currentJourneyIdRef.current !== mutationJourneyId) return;
      const kind = classifyPlanningError(cause);
      if (kind === 'conflict') setFormError(planningConflictMessage(cause));
      else if (kind === 'validation') setFormError('請檢查表單內容。');
      else if (kind === 'forbidden') setFormError('你沒有進行此規劃變更的權限。');
      else setFormError(resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed') || '規劃變更失敗。');
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  // 只重新讀取畫面，不重送任何寫入。
  async function retryRefresh() {
    if (refreshRetrying || mutating.current) return;
    const retryJourneyId = journeyId;
    const retrySeq = ++retryRefreshSeqRef.current;
    setRefreshRetrying(true);
    setLiveFeedback(null);
    const outcome = await fetchPlanning({ soft: true });
    if (currentJourneyIdRef.current !== retryJourneyId || retrySeq !== retryRefreshSeqRef.current) return;
    // 被較新的請求取代（stale）時仍須解除重試中狀態，否則之後的重試按鈕會永久停用。
    setRefreshRetrying(false);
    if (outcome !== 'ok') return;
    setPublicationReloadSeq((seq) => seq + 1);
    setLiveFeedback({ key: PLANNING_FEEDBACK_KEYS.refreshed });
  }

  async function submitAddDay(event) {
    event.preventDefault();
    if (!dayDraft.localDate) {
      setFormError('請選擇日期。');
      return;
    }
    await runMutation(async () => {
      await journeyApi.addDay(journey.journeyId, {
        expectedRowVersion: journey.rowVersion,
        localDate: dayDraft.localDate,
        title: dayDraft.title.trim() || null,
        notes: null,
      });
      setAddingDay(false);
      setDayDraft({ localDate: '', title: '' });
    });
  }

  async function submitEditDay(event) {
    event.preventDefault();
    if (!dayEdit.localDate) {
      setFormError('請選擇日期。');
      return;
    }
    await runMutation(async () => {
      await journeyApi.updateDay(journey.journeyId, editingDayId, {
        expectedRowVersion: journey.rowVersion,
        localDate: dayEdit.localDate,
        title: dayEdit.title.trim() || null,
      });
      setEditingDayId(null);
    });
  }

  async function confirmRemoveDay(day) {
    await runMutation(async () => {
      await journeyApi.removeDay(journey.journeyId, day.journeyDayId, {
        expectedRowVersion: journey.rowVersion,
        confirm: true,
      });
      setConfirmDelete(null);
    });
  }

  async function submitAddActivity(event) {
    event.preventDefault();
    const title = activityDraft.title.trim();
    if (!title) {
      setFormError('請填寫安排名稱。');
      return;
    }
    const timeError = validateActivityTimes(activityDraft.plannedStartTime, activityDraft.plannedEndTime);
    if (timeError) {
      setFormError(timeError);
      return;
    }
    const targetDayId = addingForDayId;
    await runMutation(async (isStillCurrentJourney) => {
      const body = {
        journeyDayId: targetDayId,
        title,
        description: activityDraft.description.trim() || null,
        activityType: activityDraft.activityType || 'CUSTOM',
        plannedStartTime: toApiLocalTime(activityDraft.plannedStartTime),
        plannedEndTime: toApiLocalTime(activityDraft.plannedEndTime),
        locationSummaryJson: encodeLocationSummary(activityDraft.location),
        locationId: activityDraft.locationNode?.locationId ?? null,
      };
      if (schedule.itineraryRowVersion != null) {
        body.expectedRowVersion = schedule.itineraryRowVersion;
      }
      const created = await journeyPlanningApi.createActivity(journey.journeyId, body);
      // 寫入已成功：無論目前在哪個 Journey 都消費接續 nonce，避免重複建立。
      if (personalMapHandoff?.handoffNonce) {
        consumeHandoffNonce(personalMapHandoff.handoffNonce);
      }
      // 寫入期間已換 Journey：不得清掉新 Journey 的表單或接續資料。
      if (!isStillCurrentJourney()) return created;
      clearHandoffSession();
      setPersonalMapHandoff(null);
      setPersonalMapHandoffHint('');
      setPersonalMapDuplicateWarning(false);
      mapPickGeneration.current += 1;
      setAddingForDayId(null);
      setActivityDraft(emptyActivityDraft());
      setMapPickPhase('idle');
      setMapPickError('');
      return created;
    }, {
      feedback: { key: PLANNING_FEEDBACK_KEYS.activityCreated },
      focus: {
        dayId: targetDayId,
        // 以 API 回傳的 activityId 定位新活動，不依標題猜測、不轉 Number。
        targets: (created) => {
          const createdId = snowflakeId(created?.activityId);
          return createdId ? [activitySelector(createdId)] : [];
        },
      },
    });
  }

  async function beginEditActivity(activity) {
    const token = ++editLoadGeneration.current;
    mapPickGeneration.current += 1;
    setMapPickPhase('idle');
    setMapPickError('');
    const activityId = activity.activityId;
    editingActivityIdRef.current = activityId;
    setFormError('');
    setActivityEditReady(false);
    setEditingActivityId(activityId);
    const initialNoTime = !activity.plannedStartTime && !activity.plannedEndTime;
    setActivityEdit({
      title: activity.title,
      plannedStartTime: activity.plannedStartTime || '',
      plannedEndTime: activity.plannedEndTime || '',
      timeMode: initialNoTime ? 'none' : 'timed',
      timeSuggestionSource: null,
      description: activity.description || '',
      activityType: activity.activityType || 'CUSTOM',
      location: activity.locationLabel || '',
      locationNode: locationNodeFromActivity(activity),
    });
    try {
      const detail = normalizeActivityDetail(
        await journeyPlanningApi.getActivity(journey.journeyId, activityId),
      );
      if (token !== editLoadGeneration.current) return;
      if (editingActivityIdRef.current !== activityId) return;
      const detailNoTime = !detail.plannedStartTime && !detail.plannedEndTime;
      setActivityEdit({
        title: detail.title,
        plannedStartTime: detail.plannedStartTime || '',
        plannedEndTime: detail.plannedEndTime || '',
        timeMode: detailNoTime ? 'none' : 'timed',
        timeSuggestionSource: null,
        description: detail.description || '',
        activityType: detail.activityType || 'CUSTOM',
        location: detail.locationLabel || '',
        locationNode: locationNodeFromActivity(detail),
      });
      setActivityEditReady(true);
    } catch (cause) {
      if (token !== editLoadGeneration.current) return;
      if (editingActivityIdRef.current !== activityId) return;
      setActivityEditReady(false);
      setFormError(resolveSafeUserVisibleError(cause, t, 'travel.common.loadFailed') || '無法載入安排詳情。');
    }
  }

  function closeActivityEditor() {
    if (!confirmDiscardUnsavedChanges(Boolean(editingActivityId), unsavedLeaveMessage())) return;
    editLoadGeneration.current += 1;
    mapPickGeneration.current += 1;
    editingActivityIdRef.current = null;
    setEditingActivityId(null);
    setActivityEditReady(false);
    setFormError('');
    setMapPickPhase('idle');
    setMapPickError('');
  }

  async function submitEditActivity(event) {
    event.preventDefault();
    if (!activityEditReady) {
      return;
    }
    const title = activityEdit.title.trim();
    if (!title) {
      setFormError('請填寫安排名稱。');
      return;
    }
    const timeError = validateActivityTimes(activityEdit.plannedStartTime, activityEdit.plannedEndTime);
    if (timeError) {
      setFormError(timeError);
      return;
    }
    const targetActivityId = editingActivityId;
    const targetDayId = selectedDay?.journeyDayId || '';
    await runMutation(async (isStillCurrentJourney) => {
      await journeyPlanningApi.updateActivity(journey.journeyId, targetActivityId, {
        expectedRowVersion: schedule.itineraryRowVersion,
        title,
        description: activityEdit.description.trim() || null,
        activityType: activityEdit.activityType || 'CUSTOM',
        locationSummaryJson: encodeLocationSummary(activityEdit.location),
        locationId: activityEdit.locationNode?.locationId ?? null,
        plannedStartTime: toApiLocalTime(activityEdit.plannedStartTime),
        plannedEndTime: toApiLocalTime(activityEdit.plannedEndTime),
      });
      if (!isStillCurrentJourney()) return;
      setEditingActivityId(null);
      editingActivityIdRef.current = null;
      setActivityEditReady(false);
      editLoadGeneration.current += 1;
      mapPickGeneration.current += 1;
      setMapPickPhase('idle');
      setMapPickError('');
    }, {
      feedback: { key: PLANNING_FEEDBACK_KEYS.activityUpdated },
      focus: { dayId: targetDayId, targets: () => [activitySelector(targetActivityId)] },
    });
  }

  async function confirmRemoveActivity(activity) {
    const owningDay = schedule.days.find((day) => (
      day.activities.some((item) => item.activityId === activity.activityId)
    )) || selectedDay;
    const dayId = owningDay?.journeyDayId || '';
    const fallbackTargets = neighbourTargets(owningDay, activity.activityId);
    await runMutation(async () => {
      await journeyPlanningApi.removeActivity(
        journey.journeyId,
        activity.activityId,
        schedule.itineraryRowVersion,
      );
      setConfirmDelete(null);
    }, {
      feedback: { key: PLANNING_FEEDBACK_KEYS.activityDeleted, values: { title: activity.title } },
      focus: {
        dayId,
        targets: () => fallbackTargets,
      },
    });
  }

  async function moveActivity(activity, targetJourneyDayId, targetDisplayOrder, options, onWritten) {
    await runMutation(async (isStillCurrentJourney) => {
      await journeyPlanningApi.moveActivity(journey.journeyId, activity.activityId, {
        expectedRowVersion: schedule.itineraryRowVersion,
        targetJourneyDayId,
        targetDisplayOrder,
      });
      if (onWritten && isStillCurrentJourney()) onWritten();
    }, options);
  }

  function moveWithinDay(day, activity, direction) {
    const ordered = day.activities;
    const index = ordered.findIndex((item) => item.activityId === activity.activityId);
    if (index < 0) return;
    const nextIndex = direction === 'up' ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= ordered.length) return;
    // 沿用 activityId key 與既有 <details>：節點保留，展開狀態不變；更新後回到同活動的「更多」。
    moveActivity(activity, day.journeyDayId, ordered[nextIndex].displayOrder, {
      feedback: {
        key: direction === 'up'
          ? PLANNING_FEEDBACK_KEYS.activityMovedUp
          : PLANNING_FEEDBACK_KEYS.activityMovedDown,
        values: { title: activity.title },
      },
      focus: {
        dayId: day.journeyDayId,
        targets: () => [
          testIdSelector(`planning-activity-more-${activity.activityId}`),
          activitySelector(activity.activityId),
        ],
      },
    });
  }

  // 只由明確的「移動」按鈕呼叫；寫入失敗時保留待選目標供重試，成功才清除。
  function moveToOtherDay(activity, fromDay, targetDayId) {
    if (busy || mutating.current || !targetDayId || targetDayId === fromDay.journeyDayId) return;
    const targetIndex = schedule.days.findIndex((item) => item.journeyDayId === targetDayId);
    if (targetIndex < 0) return;
    const targetDay = schedule.days[targetIndex];
    const order = (targetDay.activities.length || 0) + 1;
    moveActivity(activity, targetDayId, order, {
      feedback: {
        key: PLANNING_FEEDBACK_KEYS.activityMovedToDay,
        values: { title: activity.title, day: dayNumberOf(targetDay, targetIndex) },
      },
      focus: {
        dayId: fromDay.journeyDayId,
        switchToDayId: targetDayId,
        targets: () => [activitySelector(activity.activityId)],
        stayTargets: () => neighbourTargets(fromDay, activity.activityId),
      },
    }, () => setPendingMoveTarget(activity.activityId, ''));
  }

  async function moveJourneyDay(day, direction) {
    const currentOrder = Number(day?.displayOrder);
    if (!Number.isInteger(currentOrder) || currentOrder < 1) return;
    const targetDisplayOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    const dayCount = schedule?.days?.length || 0;
    if (targetDisplayOrder < 1 || targetDisplayOrder > dayCount) return;
    const opposite = direction === 'up' ? 'down' : 'up';
    await runMutation(async () => {
      await journeyApi.moveDay(journey.journeyId, day.journeyDayId, {
        expectedRowVersion: journey.rowVersion,
        targetDisplayOrder,
      });
    }, {
      feedback: {
        key: PLANNING_FEEDBACK_KEYS.dayMoved,
        values: { date: formatPlanningDayNavLabel(day), day: targetDisplayOrder },
      },
      focus: {
        // 選取日不變；聚焦同一天的同向按鈕，到邊界停用時改反向，再退回日期 chip。
        dayId: selectedDay?.journeyDayId || '',
        targets: () => [
          testIdSelector(`planning-day-move-${direction}-${day.journeyDayId}`),
          testIdSelector(`planning-day-move-${opposite}-${day.journeyDayId}`),
          testIdSelector(`planning-day-chip-${day.journeyDayId}`),
        ],
      },
    });
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label="載入中…" />
      </main>
    );
  }

  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>找不到旅程</h1>
        <p>此旅程可能不存在，或已無法使用。</p>
        <Link to="/journeys">返回我的旅程</Link>
      </main>
    );
  }

  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>無法開啟旅程</h1>
        <p>你沒有存取此私人旅程的權限。</p>
        <Link to="/journeys">返回我的旅程</Link>
      </main>
    );
  }

  if (phase === 'error') {
    if (errorModel) {
      const errorCopy = resolveUiErrorCopy(errorModel, t);
      return (
        <main className="journey-workspace-state" data-testid="planning-error">
          <ErrorState
            title="無法開啟行程規劃"
            description={errorCopy.description}
            retryable
            retryLabel="再試一次"
            onRetry={load}
          />
        </main>
      );
    }
    return (
      <main className="journey-workspace-state" role="alert" data-testid="planning-error">
        <h1>無法開啟行程規劃</h1>
        <p>{error}</p>
        <button type="button" onClick={load}>再試一次</button>
      </main>
    );
  }

  return (
    <main className="journey-workspace journey-planning">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">旅程工作區</p>
          <h1>{journey.title}</h1>
          <dl className="journey-workspace__facts">
            {journey.destination ? <div><dt>目的地</dt><dd>{journey.destination}</dd></div> : null}
            {journey.dateRange ? <div><dt>日期</dt><dd>{journey.dateRange}</dd></div> : null}
            <div><dt>狀態</dt><dd>{caps.lifecycleLabel || journey.lifecycleLabel}</dd></div>
            {caps.roleLabel ? <div><dt>你的角色</dt><dd>{caps.roleLabel}</dd></div> : null}
          </dl>
        </div>
        <Link className="journey-workspace__back" to="/journeys">我的旅程</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />
      <JourneyContinuationSourceNote journeyId={journey.journeyId} requesterRole={journey.requesterRole} />

      <section className="journey-workspace__panel" aria-labelledby="planning-heading">
        <div className="journey-workspace__panel-head">
          <h2 id="planning-heading">行程規劃</h2>
          <div className="journey-planning__toolbar">
            {publishEntryLabel ? (
              <button
                type="button"
                className="journey-planning__toolbar-publish"
                data-testid="planning-publish-entry"
                disabled={busy}
                onClick={() => setPublishPanelOpen(true)}
              >
                {publishEntryLabel}
              </button>
            ) : null}
            <button
              type="button"
              className="journey-planning__toolbar-secondary"
              onClick={load}
              disabled={busy}
              aria-label="重新整理"
              title="重新整理"
              data-testid="planning-refresh"
            >
              ↻
            </button>
          </div>
        </div>
        <p className="journey-workspace__panel-hint" data-testid="planning-hint">
          先選日期，再安排當天要做什麼。
        </p>
        {(() => {
          const refreshState = publicationRefreshPresentation(publicationSummary);
          if (refreshState !== 'PUBLISHED_CHANGED'
            && refreshState !== 'PUBLISHED_UNKNOWN'
            && refreshState !== 'PUBLISHED_CHANGED_BLOCKED') {
            return null;
          }
          const text = refreshState === 'PUBLISHED_CHANGED'
            ? t('travel.publication.refresh.planningChanged')
            : refreshState === 'PUBLISHED_CHANGED_BLOCKED'
              ? t('travel.publication.refresh.blocked')
              : t('travel.publication.refresh.planningUnknown');
          return (
            <p
              className="journey-planning__published-edit-notice"
              role="status"
              data-testid="planning-published-edit-notice"
              data-refresh-state={refreshState}
            >
              {text}
              {' '}
              <Link to={`/journeys/${journey.journeyId}#journey-publication`}>
                {t('travel.publication.refresh.manage')}
              </Link>
            </p>
          );
        })()}

        {banner ? <p className="journey-workspace__error" role="status">{banner}</p> : null}
        {shareNotice ? (
          <p className="journey-workspace__error" role="status">
            {shareNotice}
            {' '}
            <Link to={`/journeys/${journey.journeyId}/chat`}>查看聊天室</Link>
          </p>
        ) : null}
        {formError ? <p className="journey-workspace__error" role="alert">{formError}</p> : null}
        {refreshFailed ? (
          <div className="journey-planning__refresh-failed" role="alert" data-testid="planning-refresh-failed">
            <p>{t(PLANNING_FEEDBACK_KEYS.refreshFailed)}</p>
            <button
              type="button"
              data-testid="planning-refresh-retry"
              disabled={busy || refreshRetrying}
              onClick={retryRefresh}
            >
              {refreshRetrying
                ? t(PLANNING_FEEDBACK_KEYS.retryingRefresh)
                : t(PLANNING_FEEDBACK_KEYS.retryRefresh)}
            </button>
          </div>
        ) : null}
        {/* 固定一處 polite live region：只播報簡短成功訊息，不移動焦點。 */}
        <p
          className="journey-planning__feedback"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="planning-feedback-live"
        >
          {liveFeedback ? tf(liveFeedback.key, liveFeedback.values) : ''}
        </p>

        {schedule.days.length > 0 ? (
          <nav className="journey-planning__day-nav" aria-label="日期導覽" data-testid="planning-day-nav">
            {schedule.days.map((day, dayIndex) => {
              const active = selectedDay?.journeyDayId === day.journeyDayId;
              const dayNumberLabel = dayNumberOf(day, dayIndex);
              // 零活動日以文字標示（非僅顏色）；只是提示，不代表每天都必須有安排。
              const emptyDay = day.activityCount === 0;
              return (
                <div
                  key={day.journeyDayId}
                  className="journey-planning__day-item"
                  data-testid={`planning-day-item-${day.journeyDayId}`}
                >
                  <button
                    type="button"
                    className={`journey-planning__day-chip${active ? ' is-active' : ''}${emptyDay ? ' is-empty' : ''}`}
                    aria-pressed={active}
                    aria-label={emptyDay
                      ? tf(PLANNING_FEEDBACK_KEYS.dayChipEmptyLabel, { day: formatPlanningDayHeading(day) })
                      : formatPlanningDayHeading(day)}
                    data-testid={`planning-day-chip-${day.journeyDayId}`}
                    data-empty={emptyDay ? 'true' : undefined}
                    onClick={() => {
                      requestSelectedDayChange(day.journeyDayId);
                    }}
                  >
                    <span className="journey-planning__day-chip-order">第 {dayNumberLabel} 天</span>
                    <span className="journey-planning__day-chip-date">{formatPlanningDayNavLabel(day)}</span>
                    {emptyDay ? (
                      <span
                        className="journey-planning__day-chip-empty"
                        aria-hidden="true"
                        data-testid={`planning-day-chip-empty-${day.journeyDayId}`}
                      >
                        {t(PLANNING_FEEDBACK_KEYS.dayChipEmpty)}
                      </span>
                    ) : null}
                  </button>
                  {caps.canManageDays && schedule.days.length > 1 ? (
                    <div className="journey-planning__day-reorder" role="group" aria-label={`第 ${dayNumberLabel} 天排序`}>
                      <button
                        type="button"
                        className="journey-planning__day-reorder-btn"
                        data-testid={`planning-day-move-up-${day.journeyDayId}`}
                        aria-label={`將第 ${dayNumberLabel} 天上移`}
                        disabled={busy || dayIndex === 0}
                        onClick={() => moveJourneyDay(day, 'up')}
                      >
                        上移
                      </button>
                      <button
                        type="button"
                        className="journey-planning__day-reorder-btn"
                        data-testid={`planning-day-move-down-${day.journeyDayId}`}
                        aria-label={`將第 ${dayNumberLabel} 天下移`}
                        disabled={busy || dayIndex === schedule.days.length - 1}
                        onClick={() => moveJourneyDay(day, 'down')}
                      >
                        下移
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>
        ) : null}

        {!datedJourney && caps.canManageDays ? (
          <div className="journey-planning__secondary-actions">
            <button
              type="button"
              className="journey-planning__secondary-action"
              data-testid="planning-add-day"
              aria-label="新增一天"
              onClick={() => {
                setAddingDay(true);
                setFormError('');
                setDayDraft({
                  localDate: journey.endDate || journey.startDate || '',
                  title: '',
                });
              }}
              disabled={busy}
            >
              +
            </button>
          </div>
        ) : null}

        {addingDay ? (
          <form className="journey-planning__form" onSubmit={submitAddDay} noValidate>
            <h3>新增行程日</h3>
            <label htmlFor={dayDateId}>
              日期
              <input
                id={dayDateId}
                type="date"
                value={dayDraft.localDate}
                onChange={(e) => setDayDraft((c) => ({ ...c, localDate: e.target.value }))}
                required
              />
            </label>
            <label htmlFor={dayTitleId}>
              標題（選填）
              <input
                id={dayTitleId}
                value={dayDraft.title}
                maxLength={100}
                onChange={(e) => setDayDraft((c) => ({ ...c, title: e.target.value }))}
              />
            </label>
            <div className="journey-workspace__edit-actions">
              <button type="submit" disabled={busy}>{busy ? '儲存中…' : '建立'}</button>
              <button type="button" disabled={busy} onClick={() => setAddingDay(false)}>取消</button>
            </div>
          </form>
        ) : null}

        {schedule.days.length === 0 ? (
          <p className="journey-planning__empty" data-testid="planning-empty-days">
            {datedJourney
              ? '尚未產生行程日，請稍後再試或重新整理。'
              : '還沒有行程日。可先新增一天，再安排活動。'}
          </p>
        ) : selectedDay ? (
          <article
            className="journey-planning__day"
            data-testid="planning-selected-day"
            aria-labelledby={`planning-day-heading-${selectedDay.journeyDayId}`}
          >
            <header className="journey-planning__day-head">
              <div>
                <h3 id={`planning-day-heading-${selectedDay.journeyDayId}`} tabIndex={-1}>
                  {formatPlanningDayHeading(selectedDay)}
                </h3>
                <p className="journey-planning__day-meta">
                  {selectedDay.activityCount === 0
                    ? '尚無安排'
                    : `${selectedDay.activityCount} 項安排`}
                </p>
              </div>
              {caps.canManageDays ? (
                <details className="journey-planning__more">
                  <summary className="journey-planning__more-summary">更多</summary>
                  <div className="journey-planning__more-panel" role="group" aria-label="行程日操作">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingDayId(selectedDay.journeyDayId);
                        setDayEdit({ localDate: selectedDay.date || '', title: selectedDay.title || '' });
                        setFormError('');
                      }}
                      disabled={busy}
                    >
                      編輯這一天
                    </button>
                    {selectedDay.activityCount === 0 ? (
                      <button
                        type="button"
                        className="journey-planning__danger"
                        onClick={() => setConfirmDelete({ type: 'day', day: selectedDay })}
                        disabled={busy}
                      >
                        刪除這一天
                      </button>
                    ) : null}
                  </div>
                </details>
              ) : null}
            </header>

            {editingDayId === selectedDay.journeyDayId ? (
              <form className="journey-planning__form" onSubmit={submitEditDay} noValidate>
                <label>
                  日期
                  <input
                    type="date"
                    value={dayEdit.localDate}
                    onChange={(e) => setDayEdit((c) => ({ ...c, localDate: e.target.value }))}
                    required
                  />
                </label>
                <label>
                  標題（選填）
                  <input
                    value={dayEdit.title}
                    maxLength={100}
                    onChange={(e) => setDayEdit((c) => ({ ...c, title: e.target.value }))}
                  />
                </label>
                <div className="journey-workspace__edit-actions">
                  <button type="submit" disabled={busy}>{busy ? '儲存中…' : '儲存'}</button>
                  <button type="button" disabled={busy} onClick={() => setEditingDayId(null)}>取消</button>
                </div>
              </form>
            ) : null}

            <div className="journey-planning__day-body">
              <h4 className="journey-planning__section-label">當天安排</h4>

              {addingForDayId === selectedDay.journeyDayId ? (
                <ActivityForm
                  draft={activityDraft}
                  setDraft={setActivityDraft}
                  dayActivities={selectedDay.activities}
                  onSubmit={submitAddActivity}
                  onCancel={() => {
                    if (!confirmDiscardUnsavedChanges(Boolean(addingForDayId), unsavedLeaveMessage())) return;
                    mapPickGeneration.current += 1;
                    addingForDayIdRef.current = null;
                    setAddingForDayId(null);
                    setActivityDraft(emptyActivityDraft());
                    setFormError('');
                    setMapPickPhase('idle');
                    setMapPickError('');
                    clearHandoffSession();
                    setPersonalMapHandoff(null);
                    setPersonalMapHandoffHint('');
                    setPersonalMapDuplicateWarning(false);
                  }}
                  busy={busy}
                  personalMapHint={personalMapHandoffHint}
                  personalMapDuplicateWarning={personalMapDuplicateWarning}
                  submitLabel="新增安排"
                  titleInputRef={composeTitleInputRef}
                />
              ) : null}

              {selectedDay.activities.length === 0 ? (
                <p className="journey-planning__empty" data-testid="planning-empty-activities">
                  這一天還沒有安排。
                </p>
              ) : (
                <ul className="journey-planning__activities">
                  {selectedDay.activities.map((activity, index) => {
                    const next = selectedDay.activities[index + 1];
                    const fromId = activityLocationId(activity);
                    const toId = activityLocationId(next);
                    const showRouteLink = Boolean(fromId && toId);
                    return (
                      <Fragment key={activity.activityId}>
                    <li
                      id={`planning-activity-${activity.activityId}`}
                      tabIndex={-1}
                      className={`journey-planning__activity${selectedActivityId === activity.activityId ? ' is-selected' : ''}`}
                      data-testid={`planning-activity-${activity.activityId}`}
                    >
                      {editingActivityId === activity.activityId ? (
                        <ActivityForm
                          draft={activityEdit}
                          setDraft={setActivityEdit}
                          dayActivities={selectedDay.activities}
                          excludeActivityId={activity.activityId}
                          onSubmit={submitEditActivity}
                          onCancel={closeActivityEditor}
                          busy={busy}
                          detailLoading={!activityEditReady}
                          submitLabel="儲存安排"
                        />
                      ) : (
                        <>
                          <div className="journey-planning__activity-main">
                            <span className="journey-planning__activity-order" aria-hidden="true">{index + 1}</span>
                            <div className="journey-planning__activity-copy">
                              <p className="journey-planning__activity-time">{activityTimeLabel(activity)}</p>
                              <strong className="journey-planning__activity-title">{activity.title}</strong>
                              <ActivityLocationLines activity={activity} />
                              <ActivitySecondaryMeta activity={activity} />
                            </div>
                          </div>
                          <div className="journey-planning__activity-actions">
                            {caps.canEditActivity ? (
                              <button type="button" disabled={busy} onClick={() => beginEditActivity(activity)}>
                                編輯
                              </button>
                            ) : null}
                            {activity.hasCoordinates ? (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => selectMappedActivity(activity.activityId)}
                              >
                                在地圖查看
                              </button>
                            ) : null}
                            {renderExternalMapNavigationLink(activity)}
                            <ActivityMoreMenu
                              activity={activity}
                              day={selectedDay}
                              days={schedule.days}
                              index={index}
                              caps={caps}
                              busy={busy}
                              sharing={sharingActivityId === activity.activityId}
                              onMoveUp={() => moveWithinDay(selectedDay, activity, 'up')}
                              onMoveDown={() => moveWithinDay(selectedDay, activity, 'down')}
                              pendingTargetDayId={pendingMoveTargets[activity.activityId] || ''}
                              onPendingTargetChange={(targetDayId) => setPendingMoveTarget(
                                activity.activityId,
                                targetDayId === selectedDay.journeyDayId ? '' : targetDayId,
                              )}
                              onConfirmMove={() => moveToOtherDay(
                                activity,
                                selectedDay,
                                pendingMoveTargets[activity.activityId] || '',
                              )}
                              onClearPendingTarget={() => setPendingMoveTarget(activity.activityId, '')}
                              onShare={() => shareActivityToChat(activity)}
                              onDelete={() => setConfirmDelete({ type: 'activity', activity })}
                            />
                          </div>
                        </>
                      )}
                    </li>
                    {showRouteLink ? (
                      <li
                        className="journey-planning__route-link"
                        data-testid={`planning-route-link-${activity.activityId}`}
                      >
                        <ActivityRoutingEstimate
                          // soft refresh 不重掛整頁：路段改變時重置預估，避免顯示舊路段結果。
                          key={`${fromId}->${toId}`}
                          journeyId={journey.journeyId}
                          fromLocationId={fromId}
                          toLocationId={toId}
                        />
                      </li>
                    ) : null}
                      </Fragment>
                    );
                  })}
                </ul>
              )}

              {caps.canCreateActivity && addingForDayId !== selectedDay.journeyDayId ? (
                <button
                  type="button"
                  className="journey-planning__add-activity"
                  data-testid="planning-add-activity"
                  onClick={() => {
                    setAddingForDayId(selectedDay.journeyDayId);
                    // Init-only smart next from THIS day's activities (no cross-day bleed).
                    const draft = emptyActivityDraft(selectedDay.activities);
                    const pendingNode = personalMapHandoff
                      && !personalMapHandoff.journeyDayId
                      ? locationNodeFromHandoff(personalMapHandoff)
                      : null;
                    if (pendingNode) {
                      draft.locationNode = pendingNode;
                      setPersonalMapHandoffHint(
                        getPersonalMapMessage(DEFAULT_LOCALE, 'travel.personalMap.addToJourney.prefillHint'),
                      );
                      setPersonalMapDuplicateWarning(
                        selectedDay.activities.some(
                          (act) => String(act.locationId || '') === pendingNode.locationId,
                        ),
                      );
                    } else {
                      setPersonalMapDuplicateWarning(false);
                    }
                    setActivityDraft(draft);
                    setFormError('');
                  }}
                  disabled={busy}
                >
                  新增安排
                </button>
              ) : null}
            </div>
          </article>
        ) : null}

        <section className="journey-planning__map-block" aria-labelledby="planning-map-heading">
          <h3 id="planning-map-heading" className="journey-planning__section-label">規劃地圖</h3>
          <p className="journey-planning__map-hint">
            {mapPlacePickEnabled
              ? '顯示已儲存安排位置；編輯中可點地圖地點圖示選點（不會在空白處建立自訂座標）。'
              : '顯示已儲存且有座標的安排位置；並支援未儲存地點預覽。新增或編輯安排後即可用地圖選點。'}
          </p>
          {mapPickPhase === 'loading' ? (
            <p className="journey-planning__map-pick-status" role="status" data-testid="planning-map-pick-loading">
              正在解析地圖地點…
            </p>
          ) : null}
          {mapPickError ? (
            <p className="journey-planning__map-pick-error" role="alert" data-testid="planning-map-pick-error">
              {mapPickError}
            </p>
          ) : null}
          <Suspense fallback={<p>載入地圖…</p>}>
            <PlanningMapSurface
              markers={mapMarkersWithPreview}
              selectedKey={mapSelectedKey}
              onSelectMarker={handleMapSelectMarker}
              placePickEnabled={mapPlacePickEnabled}
              onPlacePick={handleMapPlacePick}
            />
          </Suspense>
        </section>

        {schedule.unscheduledActivities.length > 0 ? (
          <aside className="journey-planning__unscheduled" aria-labelledby="unscheduled-heading">
            <h3 id="unscheduled-heading">未排程安排</h3>
            <ul>
              {schedule.unscheduledActivities.map((activity) => (
                <li key={activity.activityId}>
                  {activity.title}
                  <ActivityLocationLines activity={activity} />
                </li>
              ))}
            </ul>
          </aside>
        ) : null}
      </section>

      <JourneyPublicationGuidedFlow
        open={publishPanelOpen}
        onClose={() => setPublishPanelOpen(false)}
        journeyId={journey?.journeyId}
        journey={journey}
        schedule={schedule}
        disabled={busy}
        onPublicationChange={setPublicationSummary}
      />

      {confirmDelete ? (
        <div className="journey-planning__dialog" role="dialog" aria-modal="true" aria-labelledby="confirm-delete-title">
          <div className="journey-planning__dialog-card">
            <h3 id="confirm-delete-title">
              {confirmDelete.type === 'day' ? '刪除這一天？' : '刪除這個安排？'}
            </h3>
            <p>
              {confirmDelete.type === 'day'
                ? `「${formatPlanningDayHeading(confirmDelete.day)}」將永久移除。`
                : `「${confirmDelete.activity.title}」將永久移除。`}
            </p>
            <div className="journey-workspace__edit-actions">
              <button
                type="button"
                className="journey-planning__danger"
                disabled={busy}
                onClick={() => (
                  confirmDelete.type === 'day'
                    ? confirmRemoveDay(confirmDelete.day)
                    : confirmRemoveActivity(confirmDelete.activity)
                )}
              >
                {busy ? '刪除中…' : '刪除'}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmDelete(null)}>取消</button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}

function ActivitySecondaryMeta({ activity }) {
  const statusLabel = activityStatusProductLabel(activity.status);
  const parts = [];
  if (activity.description) parts.push(activity.description);
  if (statusLabel) parts.push(statusLabel);
  if (parts.length === 0) return null;
  return <p className="journey-planning__activity-secondary">{parts.join(' · ')}</p>;
}

function ActivityLocationLines({ activity }) {
  const display = activityLocationDisplay(activity);
  if (!display) return null;
  return (
    <p className={`journey-planning__location${display.unresolved ? ' is-unresolved' : ''}`}>
      {display.name}
      {display.address ? ` · ${display.address}` : ''}
      {display.unresolved ? '（地點暫不可用）' : ''}
    </p>
  );
}

function ActivityMoreMenu({
  activity,
  day,
  days,
  index,
  caps,
  busy,
  sharing,
  onMoveUp,
  onMoveDown,
  pendingTargetDayId,
  onPendingTargetChange,
  onConfirmMove,
  onClearPendingTarget,
  onShare,
  onDelete,
}) {
  const { t, tf } = useTravelTranslation();
  // 選單卸載（換日、改為編輯表單、跨日移走）視同取消：清掉這筆活動尚未送出的待選目標。
  // 同日排序以 activityId key 保留節點，不會卸載。
  const clearPendingRef = useRef(onClearPendingTarget);
  clearPendingRef.current = onClearPendingTarget;
  useEffect(() => () => clearPendingRef.current?.(), []);
  const canConfirmMove = Boolean(
    pendingTargetDayId
    && pendingTargetDayId !== day.journeyDayId
    && days.some((option) => option.journeyDayId === pendingTargetDayId),
  );
  const hasMove = caps.canMoveActivity && (day.activities.length > 1 || days.length > 1);
  const hasShare = caps.canShareToChat;
  const hasDelete = caps.canRemoveActivity;
  if (!hasMove && !hasShare && !hasDelete) return null;

  return (
    <details
      className="journey-planning__more"
      // 收合選單視為取消：清掉這筆活動尚未送出的待選目標。
      onToggle={(event) => {
        if (!event.currentTarget.open) onClearPendingTarget();
      }}
    >
      <summary
        className="journey-planning__more-summary"
        data-testid={`planning-activity-more-${activity.activityId}`}
      >
        更多
      </summary>
      <div className="journey-planning__more-panel" role="group" aria-label={`${activity.title} 其他操作`}>
        {caps.canMoveActivity && day.activities.length > 1 ? (
          <>
            <button
              type="button"
              aria-label={`將 ${activity.title} 上移`}
              disabled={busy || index === 0}
              onClick={onMoveUp}
            >
              上移
            </button>
            <button
              type="button"
              aria-label={`將 ${activity.title} 下移`}
              disabled={busy || index === day.activities.length - 1}
              onClick={onMoveDown}
            >
              下移
            </button>
          </>
        ) : null}
        {caps.canMoveActivity && days.length > 1 ? (
          <label className="journey-planning__move-day">
            <span>移到其他天</span>
            <select
              aria-label={`將 ${activity.title} 移到其他天`}
              disabled={busy}
              value={pendingTargetDayId || day.journeyDayId}
              // 只更新待選目標：不發 API、不換日、不移動焦點。
              onChange={(e) => onPendingTargetChange(e.target.value)}
            >
              {days.map((option) => (
                <option key={option.journeyDayId} value={option.journeyDayId}>
                  {formatPlanningDayNavLabel(option)} · 第 {option.displayOrder} 天
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {caps.canMoveActivity && days.length > 1 ? (
          <button
            type="button"
            data-testid={`planning-activity-move-confirm-${activity.activityId}`}
            aria-label={tf(PLANNING_FEEDBACK_KEYS.moveConfirmLabel, { title: activity.title })}
            disabled={busy || !canConfirmMove}
            onClick={onConfirmMove}
          >
            {t(PLANNING_FEEDBACK_KEYS.moveConfirm)}
          </button>
        ) : null}
        {hasShare ? (
          <button
            type="button"
            disabled={busy || sharing}
            aria-label={`分享 ${activity.title} 到旅程聊天室`}
            onClick={onShare}
          >
            {sharing ? '分享中…' : '分享到旅程聊天室'}
          </button>
        ) : null}
        {hasDelete ? (
          <button
            type="button"
            className="journey-planning__danger"
            disabled={busy}
            onClick={onDelete}
          >
            刪除
          </button>
        ) : null}
      </div>
    </details>
  );
}

function ActivityForm({
  draft,
  setDraft,
  onSubmit,
  onCancel,
  busy,
  detailLoading = false,
  submitLabel,
  dayActivities = [],
  excludeActivityId = null,
  personalMapHint = '',
  personalMapDuplicateWarning = false,
  titleInputRef = undefined,
}) {
  const titleId = useId();
  const startId = useId();
  const endId = useId();
  const typeId = useId();
  const descId = useId();
  const locationId = useId();
  const timed = draft.timeMode !== 'none';
  const showSmartHint = timed && draft.timeSuggestionSource === 'smart-next';
  const fieldsLocked = busy || detailLoading;
  const submitDisabled = busy || detailLoading;
  const submitText = detailLoading ? '載入中…' : (busy ? '儲存中…' : submitLabel);

  function enableNoTime() {
    setDraft((current) => ({
      ...current,
      timeMode: 'none',
      plannedStartTime: '',
      plannedEndTime: '',
      timeSuggestionSource: null,
    }));
  }

  function enableTimed() {
    setDraft((current) => {
      // Already timed (incl.合法 start/end pair): never recompute on re-click.
      // Preserves smart-next defaults and user edits; smart-next only on no-time → timed.
      if (current.timeMode === 'timed') return current;
      // no-time → timed: apply smart-next (exclude current activity when editing).
      const defaults = buildTimedActivityDefaults(dayActivities, { excludeActivityId });
      return {
        ...current,
        timeMode: 'timed',
        plannedStartTime: defaults.plannedStartTime,
        plannedEndTime: defaults.plannedEndTime,
        timeSuggestionSource: defaults.fromPriorActivity ? 'smart-next' : null,
      };
    });
  }

  function onStartTimeChange(value) {
    setDraft((c) => ({
      ...c,
      timeMode: 'timed',
      plannedStartTime: value,
      timeSuggestionSource: null,
    }));
  }

  function onEndTimeChange(value) {
    setDraft((c) => ({
      ...c,
      timeMode: 'timed',
      plannedEndTime: value,
      timeSuggestionSource: null,
    }));
  }

  return (
    <form className="journey-planning__form" onSubmit={onSubmit} noValidate>
      {personalMapHint ? (
        <p className="journey-planning__pm-handoff-hint" data-testid="planning-pm-handoff-hint">
          {personalMapHint}
        </p>
      ) : null}
      {personalMapDuplicateWarning ? (
        <p className="journey-planning__pm-dup-warning" data-testid="planning-pm-duplicate-warning" role="status">
          {getPersonalMapMessage(DEFAULT_LOCALE, 'travel.personalMap.addToJourney.duplicateWarning')}
        </p>
      ) : null}
      <label htmlFor={titleId}>
        名稱
        <input
          id={titleId}
          ref={titleInputRef}
          value={draft.title}
          maxLength={100}
          required
          disabled={fieldsLocked}
          onChange={(e) => setDraft((c) => ({ ...c, title: e.target.value }))}
        />
      </label>
      <fieldset className="journey-planning__time-mode" disabled={fieldsLocked}>
        <legend>時間</legend>
        <div className="journey-planning__time-mode-toggle" role="group" aria-label="時間模式">
          <button
            type="button"
            className={timed ? 'is-active' : undefined}
            aria-pressed={timed}
            data-testid="activity-time-mode-timed"
            onClick={enableTimed}
          >
            有時間
          </button>
          <button
            type="button"
            className={!timed ? 'is-active' : undefined}
            aria-pressed={!timed}
            data-testid="activity-time-mode-none"
            onClick={enableNoTime}
          >
            無時間
          </button>
        </div>
        {timed ? (
          <>
            <div className="journey-workspace__dates">
              <label htmlFor={startId}>
                開始時間
                <input
                  id={startId}
                  type="time"
                  value={draft.plannedStartTime}
                  onChange={(e) => onStartTimeChange(e.target.value)}
                />
              </label>
              <label htmlFor={endId}>
                結束時間
                <input
                  id={endId}
                  type="time"
                  value={draft.plannedEndTime}
                  onChange={(e) => onEndTimeChange(e.target.value)}
                />
              </label>
            </div>
            {showSmartHint ? (
              <p className="journey-planning__time-mode-hint" data-testid="activity-smart-next-hint">
                時間接續提醒：已依前一筆安排建議開始時間 {draft.plannedStartTime}
                {draft.plannedEndTime ? `，結束 ${draft.plannedEndTime}` : ''}。
              </p>
            ) : null}
          </>
        ) : (
          <div
            className="journey-planning__time-mode-hint journey-planning__time-mode-hint--panel"
            data-testid="activity-no-time-hint"
          >
            <strong>時間接續提醒</strong>
            <p>無時間事件：開始與結束時間皆不設定；隔日第一筆重新預設 08:00。</p>
          </div>
        )}
      </fieldset>
      <label htmlFor={descId}>
        說明（選填）
        <textarea
          id={descId}
          rows={3}
          value={draft.description}
          disabled={fieldsLocked}
          onChange={(e) => setDraft((c) => ({ ...c, description: e.target.value }))}
        />
      </label>
      <fieldset className="journey-planning__location-picker" disabled={fieldsLocked}>
        <legend>地點</legend>
        <ActivityLocationPicker
          selected={draft.locationNode}
          onSelect={(node) => setDraft((current) => ({ ...current, locationNode: node }))}
          searchProvider={activitySearchProvider}
          disabled={fieldsLocked}
        />
        <CustomLocationCreatePanel
          disabled={fieldsLocked}
          onCreated={(node) => setDraft((current) => ({ ...current, locationNode: node }))}
        />
      </fieldset>
      <label htmlFor={locationId}>
        地點備註（選填）
        <input
          id={locationId}
          value={draft.location}
          disabled={fieldsLocked}
          onChange={(e) => setDraft((c) => ({ ...c, location: e.target.value }))}
          placeholder="例如路口、門牌"
        />
      </label>
      <details className="journey-planning__advanced">
        <summary>進階選項</summary>
        <label htmlFor={typeId}>
          安排類型
          <select
            id={typeId}
            value={draft.activityType}
            disabled={fieldsLocked}
            onChange={(e) => setDraft((c) => ({ ...c, activityType: e.target.value }))}
          >
            {ACTIVITY_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label || activityTypeProductLabel(type.value)}
              </option>
            ))}
          </select>
        </label>
      </details>
      <div className="journey-workspace__edit-actions">
        <button type="submit" disabled={submitDisabled}>{submitText}</button>
        <button type="button" disabled={busy} onClick={onCancel}>取消</button>
      </div>
    </form>
  );
}
